import axios from 'axios'
import { ElMessage } from 'element-plus'
import { clearParentToken, getParentToken } from '@/utils/parentSession'

const request = axios.create({
  baseURL: '/api',  // 使用相对路径，无论 IP 还是域名都能正常工作
  timeout: 30000
})

// 请求拦截器
request.interceptors.request.use(
  config => {
    const isParentApi = String(config.url || '').startsWith('/parent')
    const token = isParentApi
      ? getParentToken()
      : localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // ✅ 导出类接口兜底延长超时：大课本/大文档导出往往 > 2min
    // 说明：调用方若已显式设置 timeout，则尊重调用方配置。
    try {
      const url = String(config.url || '')
      const isExportApi =
        /\/user-files\/[^/]+\/export-pdf\b/i.test(url) ||
        /\/user-files\/[^/]+\/export-rasterized-pdf\b/i.test(url) ||
        /\/user-files\/[^/]+\/export-slides\b/i.test(url)
      if (isExportApi) {
        const DEFAULT_EXPORT_TIMEOUT = 600000 // 10min
        if (typeof config.timeout !== 'number' || Number.isNaN(config.timeout)) {
          config.timeout = DEFAULT_EXPORT_TIMEOUT
        } else {
          // 若调用方 timeout 小于默认值，则提升到默认值（避免历史代码 120s/30s）
          config.timeout = Math.max(config.timeout, DEFAULT_EXPORT_TIMEOUT)
        }
      }

      // ✅ 复习题批量生成兜底延长超时：可能涉及多次相似题检索 + AI 生成
      const isReviewQuestionsApi = /\/user-files\/[^/]+\/review-questions\b/i.test(url)
      if (isReviewQuestionsApi) {
        const DEFAULT_REVIEW_TIMEOUT = 600000 // 10min
        if (typeof config.timeout !== 'number' || Number.isNaN(config.timeout)) {
          config.timeout = DEFAULT_REVIEW_TIMEOUT
        } else {
          config.timeout = Math.max(config.timeout, DEFAULT_REVIEW_TIMEOUT)
        }
      }
    } catch (e) {
      // 忽略兜底逻辑异常
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    // 如果是下载文件请求，直接返回响应数据
    if (response.config.responseType === 'blob') {
      return response.data
    }

    const res = response.data

    // 检查响应状态
    // conflict 属于需要调用方展示详情的业务结果（如 Word 导入的重复题预检），不能只弹 message
    if (res.status === 'success' || res.status === 'conflict' || res.success === true) {
      return res
    }
    
    // 如果响应不成功，但有具体的错误信息
    if (res.message) {
      ElMessage.error(res.message)
      return Promise.reject(new Error(res.message))
    }
    
    // 默认错误处理
    ElMessage.error('请求失败')
    return Promise.reject(new Error('请求失败'))
  },
  error => {
    // 只打印排错所需的定位信息：不要打印 error.config / error 本身，
    // 其 data 字段含登录、设密码、改密码的明文口令，headers 含 token。
    console.error('请求失败:', {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      status: error.response?.status,
      code: error.code,
      message: error.message,
      isNetworkError: error.code === 'ERR_NETWORK',
      isTimeout: error.code === 'ECONNABORTED'
    })

    // 处理401错误
    if (error.response?.status === 401) {
      const isParentApi = String(error.config?.url || '').startsWith('/parent')
      if (isParentApi) {
        if (error.config?.skipAuthRedirect) {
          return Promise.reject(error)
        }
        clearParentToken()
        if (!window.location.pathname.startsWith('/parent/login')) {
          const redirect = encodeURIComponent(window.location.pathname + window.location.search)
          window.location.href = `/parent/login?redirect=${redirect}`
        }
        return Promise.reject(error)
      }
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('role')
      localStorage.removeItem('isAdmin')
      localStorage.removeItem('tokenExpiresAt')

      // 修正登录路径
      window.location.href = '/auth/login'
      return Promise.reject(error)
    }

    // 根据错误类型返回不同的错误信息
    let errorMessage = '请求失败'
    const failedUrl = String(error.config?.url || '')
    const isExportApi =
      /\/user-files\/[^/]+\/export-pdf\b/i.test(failedUrl) ||
      /\/user-files\/[^/]+\/export-rasterized-pdf\b/i.test(failedUrl) ||
      /\/user-files\/[^/]+\/export-slides\b/i.test(failedUrl)
    if (error.code === 'ERR_NETWORK') {
      errorMessage = '网络连接失败，请检查网络设置或确认后端服务是否启动'
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = isExportApi
        ? '导出超时：文档内容较大或服务器繁忙，请稍后重试（可优先使用“兼容文档（栅格化）”导出）'
        : '请求超时，请稍后重试'
    } else if (error.response) {
      switch (error.response.status) {
        case 403:
          errorMessage = '没有权限访问'
          break
        case 404:
          errorMessage = `请求的资源不存在（${error.config?.url || 'unknown-url'}）`
          break
        case 504:
          errorMessage = '服务器处理超时（504），请稍后重试或缩小本次处理范围'
          break
        default:
          errorMessage = error.response.data?.message || `服务器响应错误 (${error.response.status})`
      }
    }

    ElMessage.error(errorMessage)
    return Promise.reject(error)
  }
)

export default request 
